import React, { useEffect, useRef, useState } from 'react';
import '../../styles/Landing.css';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const LandingSection = ({
    title,
    subtitle,
    buttonText,
    buttonLink,
    image,
    mobileImage,
    imageMaxWidth = '100%',
    variant = 'stacked',
    buttonVariant = 'link',
    bulletPoints = [],
    bottomText = '',
    onClick = () => buttonLink && (window.location.href = buttonLink),
    id = '',
    customContent = null
}) => {
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);

  // Scroll-triggered visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    if (contentRef.current) observer.observe(contentRef.current);
    if (imageRef.current) observer.observe(imageRef.current);

    return () => observer.disconnect();
  }, []);

  // Subtle parallax effect on images - throttled with RAF for smooth scrolling
  useEffect(() => {
    if (!image || typeof window === 'undefined') return;

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          if (!imageRef.current) {
            ticking = false;
            return;
          }
          const rect = imageRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const elementCenter = rect.top + rect.height / 2;
          const viewportCenter = windowHeight / 2;
          const distance = (elementCenter - viewportCenter) / windowHeight;
          setParallaxOffset(distance * -8);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [image]);

  return (
    <section className={`landing-section ${variant === 'split' ? 'landing-section-split' : ''}`} id={id}>
      <div ref={contentRef} className={`landing-content ${variant === 'split' ? 'landing-content-split' : ''}`}>
        <h2 className="landing-title">{title}</h2>
        <div className="landing-section-text">
            <p className="landing-subtitle">{subtitle}</p>
        
            {variant === 'split' && bulletPoints.length > 0 && (
            <ul className="landing-bullets">
                {bulletPoints.map((point, index) => (
                <li key={index}>{point}</li>
                ))}
            </ul>
            )}

            {bottomText && <p className="landing-bottom-text">{bottomText}</p>}
        </div>
        {buttonText && (
            buttonVariant === 'link' ? (
                <button onClick={onClick} className="landing-button">
                    {buttonText} <ArrowForwardIcon sx={{ fontSize: 20 }} />
                </button>
            ) : (
                <div>
                    <br />
                    <button onClick={onClick} className="btn-square btn-primary btn-very-wide btn-lg btn-bold">
                        {buttonText}
                    </button>
                </div>
            )
        )}
      </div>

      {customContent || (
          <>
              {image && variant === 'split' && (
                  <div
                      ref={imageRef}
                      className="landing-image-split"
                      onClick={buttonLink ? onClick : undefined}
                      style={{ cursor: buttonLink ? 'pointer' : 'default' }}
                  >
                      <img
                          src={image}
                          alt=""
                          className="desktop-image"
                          style={{
                            maxWidth: imageMaxWidth,
                            transform: `translateY(${parallaxOffset}px)`
                          }}
                      />
                      {mobileImage && <img src={mobileImage} alt="" className="mobile-image" />}
                  </div>
              )}

              {image && variant === 'stacked' && (
                  <div
                      ref={imageRef}
                      className="landing-image"
                      onClick={buttonLink ? onClick : undefined}
                      style={{ cursor: buttonLink ? 'pointer' : 'default' }}
                  >
                      <img
                          src={image}
                          alt=""
                          className="desktop-image"
                          style={{
                            maxWidth: imageMaxWidth,
                            transform: `translateY(${parallaxOffset}px)`
                          }}
                      />
                      {mobileImage && <img src={mobileImage} alt="" className="mobile-image" />}
                  </div>
              )}
          </>
      )}
    </section>
  );
};

export default LandingSection;
